import React from "react";
import {
  FaCheck,
  FaChevronRight,
  FaFlagCheckered,
  FaTimes,
} from "react-icons/fa";

function OptionButton({ text, state, onClick }) {
  const styles = {
    idle:
      "border-slate-200 bg-white text-slate-900 hover:border-indigo-300 hover:bg-indigo-50 dark:border-slate-700 dark:bg-slate-950 dark:text-white dark:hover:bg-indigo-900/20",
    correct:
      "border-emerald-300 bg-emerald-50 text-emerald-800 dark:border-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-200",
    wrong:
      "border-rose-300 bg-rose-50 text-rose-800 dark:border-rose-700 dark:bg-rose-900/20 dark:text-rose-200",
    disabled:
      "border-slate-200 bg-slate-100 text-slate-400 dark:border-slate-800 dark:bg-slate-800/60 dark:text-slate-500",
  };

  const icon =
    state === "correct" ? (
      <FaCheck />
    ) : state === "wrong" ? (
      <FaTimes />
    ) : null;

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={state === "disabled"}
      className={`flex min-h-[54px] w-full items-center justify-between gap-3 rounded-2xl border px-4 py-3 text-left text-sm font-bold transition disabled:cursor-not-allowed sm:text-base ${
        styles[state || "idle"]
      }`}
    >
      <span>{text}</span>
      {icon && (
        <span className="grid h-8 w-8 shrink-0 place-items-center rounded-xl bg-white/70 dark:bg-white/10">
          {icon}
        </span>
      )}
    </button>
  );
}

export default function QuizzQuestionCard({
  question,
  selected,
  onAnswer,
  onNext,
  hasNext,
}) {
  if (!question) return null;

  const getState = (option) => {
    if (!selected) return "idle";
    if (option === question.correctAnswer) return "correct";
    if (option === selected && selected !== question.correctAnswer) {
      return "wrong";
    }

    return "disabled";
  };

  const isCorrect = selected && selected === question.correctAnswer;

  return (
    <div className="grid grid-cols-1 gap-5 lg:grid-cols-12">
      <div className="lg:col-span-7">
        <div className="overflow-hidden rounded-[26px] border border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-950">
          <div
            className="mx-auto w-full max-w-[320px] sm:max-w-[420px] lg:max-w-none"
            style={{ aspectRatio: "551 / 617" }}
          >
            <img
              src={question.image}
              alt={`Question technique ${question.famille || ""}`}
              className="h-full w-full object-contain"
              loading="lazy"
            />
          </div>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          {question.famille && (
            <span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-bold text-blue-700 ring-1 ring-blue-200 dark:bg-blue-900/20 dark:text-blue-200 dark:ring-blue-800/60">
              {question.famille}
            </span>
          )}

          {question.ceinture && (
            <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-700 ring-1 ring-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:ring-slate-700">
              {question.ceinture}
            </span>
          )}
        </div>

        {selected && (
          <div
            className={`mt-4 rounded-2xl px-4 py-3 text-sm font-bold ring-1 ${
              isCorrect
                ? "bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-200 dark:ring-emerald-800/60"
                : "bg-rose-50 text-rose-700 ring-rose-200 dark:bg-rose-900/20 dark:text-rose-200 dark:ring-rose-800/60"
            }`}
          >
            {isCorrect ? (
              <>Bonne réponse : {question.correctAnswer}</>
            ) : (
              <>
                Mauvaise réponse. La bonne réponse était :{" "}
                {question.correctAnswer}
              </>
            )}
          </div>
        )}
      </div>

      <div className="space-y-3 lg:col-span-5">
        {question.options.map((option) => (
          <OptionButton
            key={option}
            text={option}
            state={getState(option)}
            onClick={() => onAnswer(option)}
          />
        ))}

        <div className="pt-2">
          <button
            type="button"
            onClick={onNext}
            disabled={!selected}
            className={`inline-flex min-h-[54px] w-full items-center justify-center gap-2 rounded-2xl px-5 py-3 text-sm font-bold text-white shadow-sm transition disabled:cursor-not-allowed disabled:bg-slate-300 disabled:text-white/80 dark:disabled:bg-slate-800 ${
              selected
                ? "bg-gradient-to-r from-[#013E8E] via-indigo-600 to-[#4CA82F] hover:shadow-md"
                : ""
            }`}
          >
            {hasNext ? (
              <>
                Question suivante
                <FaChevronRight />
              </>
            ) : (
              <>
                Voir les résultats
                <FaFlagCheckered />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}